import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { ethers } from 'ethers';
import { useContractServices } from '../../context/ContractServicesContext';
import { 
  Percent, 
  CreditCard, 
  DollarSign,
  Wallet,
  ArrowRight
} from 'lucide-react';
import DebugTreasuryPanel from './DebugTreasuryPanel';
import skyLoader from "../../assets/home-assets/skyLoader.gif";

const FeesAndTreasuryPanel = ({ adminService, refreshKey = 0, onTransactionSubmit = () => {} }) => {
  const { services, isReady } = useContractServices();
  
  const [loading, setLoading] = useState(false);
  const [treasuryInfo, setTreasuryInfo] = useState({
    buyFeePercentage: null,
    sellFeePercentage: null,
    treasuryBalance: null
  });
  const [error, setError] = useState(null);
  const [showDebug, setShowDebug] = useState(false);
  
  // Form states
  const [newBuyFee, setNewBuyFee] = useState('');
  const [newSellFee, setNewSellFee] = useState('');
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawAddress, setWithdrawAddress] = useState('');
  
  // Fetch treasury info
  useEffect(() => {
    fetchTreasuryInfo();
  }, [isReady, services, refreshKey]);
  
  const fetchTreasuryInfo = async () => {
    if (!isReady) return;
    
    try {
      setLoading(true);
      setError(null); // Clear any previous errors
      console.log("Fetching treasury info...");
      
      // Get fee info from InnovationUnits contract
      const buyFeePercentage = await services.innovationUnits.contract.buyFeePercentage();
      const sellFeePercentage = await services.innovationUnits.contract.sellFeePercentage();
      console.log("Buy fee:", buyFeePercentage.toString(), "Sell fee:", sellFeePercentage.toString());
      
      // Get treasury balance - handle possible missing method
      let treasuryBalance = "0";
      try {
        console.log("Protocol Treasury service:", services.protocolTreasury);
        
        if (services.protocolTreasury && typeof services.protocolTreasury.getBalance === 'function') {
          console.log("Getting balance from protocolTreasury.getBalance()");
          treasuryBalance = await services.protocolTreasury.getBalance();
        } else if (services.protocolTreasury && services.protocolTreasury.contract) {
          console.log("Getting balance from protocolTreasury.contract.getBalance()");
          const balanceWei = await services.protocolTreasury.contract.getBalance();
          treasuryBalance = ethers.utils.formatEther(balanceWei);
        } else {
          console.warn("Protocol Treasury service is not properly initialized");
          
          // Fallback: get balance using CEL token contract if protocol treasury address is available
          if (services.protocolTreasury && services.protocolTreasury.contract && services.protocolTreasury.contract.address) {
            console.log("Getting balance via CEL token contract");
            const treasuryAddress = services.protocolTreasury.contract.address;
            const balanceWei = await services.celToken.contract.balanceOf(treasuryAddress);
            treasuryBalance = ethers.utils.formatEther(balanceWei);
          }
        }
      } catch (balanceError) {
        console.error("Error getting treasury balance:", balanceError);
        setError(`Failed to fetch treasury balance: ${balanceError.message}`);
        treasuryBalance = "0";
      }
      
      console.log("Treasury balance:", treasuryBalance);
      
      setTreasuryInfo({
        buyFeePercentage: buyFeePercentage.toNumber() / 100, // Convert from basis points to percentage
        sellFeePercentage: sellFeePercentage.toNumber() / 100, // Convert from basis points to percentage
        treasuryBalance: treasuryBalance
      });
    } catch (error) {
      console.error('Error fetching treasury info:', error);
      setError(`Failed to fetch treasury information: ${error.message}`);
      toast.error('Failed to fetch treasury information');
    } finally {
      setLoading(false);
    }
  };
  
  // Set IU buy fee
  const handleSetBuyFee = async (e) => {
    e.preventDefault();
    if (!newBuyFee) return;
    
    try {
      setLoading(true);
      await adminService.updateBuyFeePercentage(newBuyFee);
      toast.success(`IU buy fee set to ${newBuyFee}%`);
      
      // Call transaction submit callback
      onTransactionSubmit();
      
      // Refresh treasury info
      await fetchTreasuryInfo();
      
      setNewBuyFee('');
    } catch (error) {
      console.error('Error setting buy fee:', error);
      toast.error('Failed to update IU buy fee');
    } finally {
      setLoading(false);
    }
  };
  
  // Set IU sell fee
  const handleSetSellFee = async (e) => {
    e.preventDefault();
    if (!newSellFee) return;
    
    try {
      setLoading(true);
      await adminService.updateSellFeePercentage(newSellFee);
      toast.success(`IU sell fee set to ${newSellFee}%`);
      
      // Call transaction submit callback
      onTransactionSubmit();
      
      // Refresh treasury info
      await fetchTreasuryInfo();
      
      setNewSellFee('');
    } catch (error) {
      console.error('Error setting sell fee:', error);
      toast.error('Failed to update IU sell fee');
    } finally {
      setLoading(false);
    }
  };
  
  // Withdraw from treasury
  const handleWithdraw = async (e) => {
    e.preventDefault();
    if (!withdrawAmount || !withdrawAddress) return;
    
    try {
      setLoading(true);
      
      // Validate address
      if (!ethers.utils.isAddress(withdrawAddress)) {
        toast.error('Invalid Ethereum address');
        return;
      }
      
      await adminService.sendFeesFromTreasury(withdrawAddress, withdrawAmount);
      toast.success(`Successfully withdrew ${withdrawAmount} CEL to ${withdrawAddress}`);
      
      // Call transaction submit callback
      onTransactionSubmit();
      
      // Refresh treasury info
      await fetchTreasuryInfo();
      
      // Reset form
      setWithdrawAmount('');
      setWithdrawAddress('');
    } catch (error) {
      console.error('Error withdrawing from treasury:', error);
      toast.error('Failed to withdraw from treasury');
    } finally {
      setLoading(false);
    }
  };
  
  const toggleDebug = () => {
    setShowDebug(!showDebug);
  };
  
  return (
    <div>
      <h2 className="text-2xl font-semibold text-gray-800 mb-6 flex justify-between items-center">
        <span>Fees & Treasury Management</span>
        <button
          onClick={toggleDebug}
          className="text-sm px-2 py-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
        >
          {showDebug ? 'Hide Debug' : 'Show Debug'}
        </button>
      </h2>
      
      {showDebug && <DebugTreasuryPanel />}
      
      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
          <p><strong>Error:</strong> {error}</p>
          <button 
            onClick={() => fetchTreasuryInfo()}
            className="mt-2 px-3 py-1 bg-red-500 text-white rounded text-sm"
          >
            Retry
          </button>
        </div>
      )}
      
      {/* Current Treasury Status */}
      <div className="mb-8 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <h3 className="text-lg font-medium text-gray-700 mb-4 flex items-center">
          <Wallet className="h-5 w-5 text-indigo-500 mr-2" />
          Treasury Status
        </h3>
        
        {loading ? (
          <div className="flex justify-center">
            <div className="flex justify-center items-center h-100">
              <img 
                src={skyLoader} 
                alt="Loading..." 
                className="w-100 h-100 object-contain"
              />
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-gray-600">IU Buy Fee:</p>
              <p className="font-medium">
                {treasuryInfo.buyFeePercentage !== null ? `${treasuryInfo.buyFeePercentage}%` : 'Loading...'}
              </p>
              <p className="text-xs text-gray-500 mt-1">Fee charged when buying IUs</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">IU Sell Fee:</p>
              <p className="font-medium">
                {treasuryInfo.sellFeePercentage !== null ? `${treasuryInfo.sellFeePercentage}%` : 'Loading...'}
              </p>
              <p className="text-xs text-gray-500 mt-1">Fee charged when selling IUs</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Treasury CEL Balance:</p>
              <p className="font-medium">
                {treasuryInfo.treasuryBalance !== null ? `${parseFloat(treasuryInfo.treasuryBalance).toFixed(4)} CEL` : 'Loading...'}
              </p>
              <p className="text-xs text-gray-500 mt-1">Total CEL tokens in Protocol Treasury</p>
            </div>
          </div>
        )}
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Fee Configuration */}
        <div className="p-4 bg-white rounded-lg border border-gray-200">
          <h3 className="text-lg font-medium text-gray-700 mb-4 flex items-center">
            <Percent className="h-5 w-5 text-indigo-500 mr-2" />
            Fee Configuration
          </h3>
          
          {/* IU Buy Fee */}
          <form onSubmit={handleSetBuyFee} className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              IU Buy Fee (%)
            </label>
            <div className="flex gap-2">
              <input
                type="number"
                value={newBuyFee}
                onChange={(e) => setNewBuyFee(e.target.value)}
                className="p-2 border border-gray-300 rounded-md w-full"
                placeholder="e.g., 5"
                min="0"
                max="30"
                step="0.1"
              />
              <button
                type="submit"
                disabled={loading || !newBuyFee}
                className="px-4 py-2 bg-indigo-500 text-white rounded-md hover:bg-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Set
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Fee charged when users buy Innovation Units (max 30%)
            </p>
          </form>
          
          {/* IU Sell Fee */}
          <form onSubmit={handleSetSellFee}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              IU Sell Fee (%)
            </label>
            <div className="flex gap-2">
              <input
                type="number"
                value={newSellFee}
                onChange={(e) => setNewSellFee(e.target.value)}
                className="p-2 border border-gray-300 rounded-md w-full"
                placeholder="e.g., 20"
                min="0"
                max="30"
                step="0.1"
              />
              <button
                type="submit"
                disabled={loading || !newSellFee}
                className="px-4 py-2 bg-indigo-500 text-white rounded-md hover:bg-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Set
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Fee charged when users sell (redeem) Innovation Units (max 30%)
            </p>
          </form>
        </div>
        
        {/* Treasury Management */}
        <div className="p-4 bg-white rounded-lg border border-gray-200">
          <h3 className="text-lg font-medium text-gray-700 mb-4 flex items-center">
            <DollarSign className="h-5 w-5 text-indigo-500 mr-2" />
            Treasury Management
          </h3>
          
          <form onSubmit={handleWithdraw}>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Withdraw Amount (CEL)
              </label>
              <input
                type="number"
                value={withdrawAmount}
                onChange={(e) => setWithdrawAmount(e.target.value)}
                className="p-2 border border-gray-300 rounded-md w-full"
                placeholder="Amount to withdraw"
                min="0"
                step="0.01"
              />
              {treasuryInfo.treasuryBalance && (
                <div className="flex justify-end mt-1">
                  <button
                    type="button"
                    onClick={() => setWithdrawAmount(treasuryInfo.treasuryBalance)}
                    className="text-xs text-indigo-600 hover:text-indigo-800"
                  >
                    Max: {parseFloat(treasuryInfo.treasuryBalance).toFixed(4)} CEL
                  </button>
                </div>
              )}
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Recipient Address
              </label>
              <input
                type="text"
                value={withdrawAddress}
                onChange={(e) => setWithdrawAddress(e.target.value)}
                className="p-2 border border-gray-300 rounded-md w-full"
                placeholder="0x..."
              />
            </div>
            
            <div className="mt-6">
              <button
                type="submit"
                disabled={loading || !withdrawAmount || !withdrawAddress}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-indigo-500 text-white rounded-md hover:bg-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ArrowRight className="h-4 w-4" />
                Withdraw Funds
              </button>
            </div>
            
            <p className="text-xs text-gray-500 mt-2">
              Only use this feature for approved treasury operations.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default FeesAndTreasuryPanel; 